import React from "react";
import {
  Archive,
  Check,
  FileText,
  Globe,
  Layers,
  Link2,
  MapPin,
  Shield,
  ShieldCheck,
  UserCog,
  Users,
} from "lucide-react";

export interface JourneyMetrics {
  /** Implementation status counts derived from controlImpls */
  implementationCounts: {
    implemented: number;
    partial: number;
    planned: number;
    alternative: number;
    notImplemented: number;
    notApplicable: number;
  };
  totalBaselineControls: number;
  controlImplsCount: number;
  componentsCount: number;
  infoTypesCount: number;
  inventoryCount: number;
  systemUsersCount: number;
  interconnectionsCount: number;
  locationsCount: number;
  partiesCount: number;
  leveragedAuthsCount: number;
  hasSystemCharacteristics: boolean;
}

interface SSPDetailJourneyProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  metrics: JourneyMetrics;
}

const JOURNEY_STEPS: {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { key: "System Characteristics", label: "System\nCharacteristics", icon: Shield },
  { key: "Information Types", label: "Information\nTypes", icon: FileText },
  { key: "Components", label: "Components", icon: Layers },
  { key: "Inventory", label: "Inventory", icon: Archive },
  { key: "Control Implementations", label: "Control\nImplementations", icon: Link2 },
  { key: "System Users", label: "System Users", icon: Users },
  { key: "Interconnections", label: "Interconnections", icon: Globe },
  { key: "Locations", label: "Locations", icon: MapPin },
  { key: "Roles & Parties", label: "Roles & Parties", icon: UserCog },
  { key: "Leveraged Authorizations", label: "Leveraged\nAuthorizations", icon: ShieldCheck },
];

function isStepComplete(stepKey: string, m: JourneyMetrics): boolean {
  switch (stepKey) {
    case "System Characteristics":
      return m.hasSystemCharacteristics;
    case "Information Types":
      return m.infoTypesCount > 0;
    case "Components":
      return m.componentsCount > 0;
    case "Inventory":
      return m.inventoryCount > 0;
    case "Control Implementations":
      return (
        m.totalBaselineControls > 0 ||
        m.controlImplsCount > 0 ||
        m.implementationCounts.implemented > 0 ||
        m.implementationCounts.partial > 0 ||
        m.implementationCounts.planned > 0 ||
        m.implementationCounts.alternative > 0 ||
        m.implementationCounts.notApplicable > 0
      );
    case "System Users":
      return m.systemUsersCount > 0;
    case "Interconnections":
      return m.interconnectionsCount > 0;
    case "Locations":
      return m.locationsCount > 0;
    case "Roles & Parties":
      return m.partiesCount > 0;
    case "Leveraged Authorizations":
      return m.leveragedAuthsCount > 0;
    default:
      return false;
  }
}

function JourneyStep({
  step,
  active,
  complete,
  onClick,
}: {
  step: (typeof JOURNEY_STEPS)[number];
  active: boolean;
  complete: boolean;
  onClick: () => void;
}) {
  const Icon = step.icon;

  const ring = active
    ? "border-indigo-500 dark:border-indigo-400 ring-4 ring-indigo-500/15 dark:ring-indigo-400/20"
    : complete
      ? "border-emerald-500 dark:border-emerald-400"
      : "border-[#e2e8f0] dark:border-white/[0.08]";

  const iconColor = active
    ? "text-indigo-600 dark:text-indigo-400"
    : complete
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-[#94a3b8] dark:text-white/40";

  const labelColor = active
    ? "text-foreground font-semibold"
    : complete
      ? "text-foreground/80 font-medium"
      : "text-[#94a3b8] dark:text-white/50 font-medium";

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 min-w-[72px] flex-shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 rounded-md"
    >
      <span
        className={`relative flex size-9 items-center justify-center rounded-full bg-card border-2 transition-all ${ring}`}
      >
        <Icon className={`size-4 ${iconColor}`} />
        {complete && !active && (
          <span className="absolute -bottom-0.5 -right-0.5 flex size-3 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-card">
            <Check className="size-2" strokeWidth={3} />
          </span>
        )}
      </span>
      <span
        className={`text-[10px] leading-tight text-center whitespace-pre-line ${labelColor}`}
      >
        {step.label}
      </span>
    </button>
  );
}

function JourneyConnector({ complete }: { complete: boolean }) {
  return (
    <div className="flex-1 h-px mx-0.5 self-start mt-[18px] border-t border-dashed border-[#cbd5e1] dark:border-white/[0.12] min-w-[12px]">
      {complete && (
        <div className="h-full bg-emerald-500/60 dark:bg-emerald-400/50" />
      )}
    </div>
  );
}

export function SSPDetailJourney({
  activeTab,
  onTabChange,
  metrics,
}: SSPDetailJourneyProps) {
  return (
    <div className="rounded-xl border border-[#e8edf2] dark:border-white/[0.08] bg-card px-4 py-2.5">
      <div className="flex items-start overflow-x-auto overflow-y-visible py-1.5 -mx-1 px-1 scrollbar-hide">
        {JOURNEY_STEPS.map((step, idx) => {
          const complete = isStepComplete(step.key, metrics);
          const isLast = idx === JOURNEY_STEPS.length - 1;
          const nextComplete =
            !isLast && isStepComplete(JOURNEY_STEPS[idx + 1].key, metrics);
          return (
            <React.Fragment key={step.key}>
              <JourneyStep
                step={step}
                active={activeTab === step.key}
                complete={complete}
                onClick={() => onTabChange(step.key)}
              />
              {!isLast && <JourneyConnector complete={complete && nextComplete} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
